import { Text, Icon, Flex, VStack, Box, Badge } from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { RenderNodeProps, useNodeColors } from './RenderNodeProps';
import { FiCheckCircle } from 'react-icons/fi';

export const getBadges = (nodeType: TapeBuilderUI.TapeBuilderNodeType) => {
  const uiNode = TapeBuilderUI.getUINodeByType(nodeType);
  let colorScheme: string = 'white';
  finleyAssert(!uiNode.isOperation, 'Operation nodes do not have badges');
  if (uiNode.badgeTexts.includes('BackupServicing')) {
    colorScheme = 'green';
  } else if (uiNode.isFinal) {
    colorScheme = 'green';
  } else if (uiNode.isRaw) {
    colorScheme = 'blue';
  } else {
    colorScheme = 'yellow';
  }
  return (
    <Text fontSize="md" fontWeight="light">
      {uiNode.badgeTexts.map((badgeText) => (
        <Badge key={badgeText} variant="outline" colorScheme={colorScheme}>
          {badgeText}
        </Badge>
      ))}
    </Text>
  );
};

export const TapeNode = ({
  nodeText,
  nodeType,
  selected = false,
  hasInputs = false,
  draggable = false,
  children,
}: RenderNodeProps) => {
  const {
    bgColor,
    textColor,
    borderColor,
    selectedBorderColor,
    iconColor,
    unSelectedBorderSize,
    selectedBorderSize,
  } = useNodeColors();

  return (
    <Box
      bg={bgColor}
      color={textColor}
      draggable={draggable}
      onDragStart={(event) => {
        event.dataTransfer.setData('application/reactflow', nodeType);
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.effectAllowed = 'move';
      }}
      borderRadius={10}
      border={
        selected
          ? `solid ${selectedBorderSize}`
          : `solid ${unSelectedBorderSize}`
      }
      borderColor={selected ? selectedBorderColor : borderColor}
      padding="10px"
      minW="150px"
      maxW="240px"
      cursor="pointer"
      transition="box-shadow 0.15s, border-color 0.15s"
      _hover={{
        boxShadow: '0 0 0 3px var(--chakra-colors-blue-300)',
      }}
    >
      <Flex alignItems="center" gap={2}>
        {/* Small "ready" indicator when the node has inputs buffered. */}
        {hasInputs && (
          <Icon
            as={FiCheckCircle}
            color={iconColor}
            boxSize="18px"
            flexShrink={0}
          />
        )}
        <VStack flex={1} spacing={1}>
          <Text
            width="100%"
            minWidth={0}
            textAlign="center"
            noOfLines={2}
            // `anywhere` (unlike `break-word`) also shrinks the intrinsic
            // min-content width, so long unbroken camelCase slugs actually wrap
            // inside the node instead of overflowing it.
            overflowWrap="anywhere"
            title={nodeText}
          >
            {nodeText}
          </Text>

          {getBadges(nodeType)}
          {children}
        </VStack>
      </Flex>
    </Box>
  );
};
